import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { StyleSheet, View, TextInput, Pressable, Platform, Keyboard } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { BORDER_RADIUS, MEMORY_CHAR_LIMIT } from '@/constants/config';
import { getMemoryThemeColors } from './memoryTheme';

const isWeb = Platform.OS === 'web';
const CHAR_COUNT_WARNING_COLOR = '#F59E0B';

function getCharCountProps(length, limit) {
  const ratio = length / limit;
  if (ratio > 0.9) return { color: 'danger', style: undefined };
  if (ratio > 0.75) return { color: undefined, style: { color: CHAR_COUNT_WARNING_COLOR } };
  return { color: 'secondary', style: undefined };
}

/**
 * MemoryQuickAdd
 *
 * Delightful inline memory creation card:
 * - Auto-growing or multiline textarea with responsive focus states
 * - Inspiration chips to spark ideas
 * - Real-time character limit indicator
 * - Keyboard shortcuts (Cmd/Ctrl + Enter to submit, Escape to cancel)
 */
function MemoryQuickAdd({
  text,
  onChangeText,
  onAddMemory,
  onCancel,
  isCreating = false,
}) {
  const { colors, isDark } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();

  const memColors = useMemo(() => getMemoryThemeColors(isDark, colors), [isDark, colors]);

  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  const styles = useMemo(
    () => createStyles(colors, memColors, isDark, isXS),
    [colors, memColors, isDark, isXS]
  );

  useEffect(() => {
    // Auto-focus on mount
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const inspirationChips = useMemo(() => [
    t('pages.memory.inspiration1'),
    t('pages.memory.inspiration2'),
    t('pages.memory.inspiration3'),
    t('pages.memory.inspiration4'),
    t('pages.memory.inspiration5'),
  ], [t]);

  const handleChipPress = useCallback((chip) => {
    if (isCreating) return;
    onChangeText(text.trim() ? `${text.trim()}. ${chip}` : chip);
    inputRef.current?.focus();
  }, [text, onChangeText, isCreating]);

  const handleSubmit = useCallback(async () => {
    if (!text.trim() || text.length > MEMORY_CHAR_LIMIT || isCreating) return;
    const ok = await onAddMemory(text.trim());
    if (ok) {
      onChangeText('');
      Keyboard.dismiss();
    }
  }, [text, isCreating, onAddMemory, onChangeText]);

  const handleKeyDown = useCallback((e) => {
    if (!isWeb || isCreating) return;
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel?.();
    }
  }, [handleSubmit, onCancel, isCreating]);

  const charProps = getCharCountProps(text.length, MEMORY_CHAR_LIMIT);
  const isTooLong = text.length > MEMORY_CHAR_LIMIT;
  const canSubmit = text.trim().length > 0 && !isTooLong && !isCreating;

  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <View style={styles.headerTitleRow}>
          <View style={styles.iconCircle}>
            <Icon name="pen" size={13} color={memColors.accent} />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text size="s" weight="bold" color="primary">
              {t('pages.memory.quickAddTitle')}
            </Text>
            <Text size="xs" color="secondary">
              {t('pages.memory.quickAddSubtitle')}
            </Text>
          </View>
        </View>

        <Pressable
          style={({ hovered }) => [
            styles.closeButton,
            hovered && styles.closeButtonHovered,
          ]}
          disabled={isCreating}
          onPress={onCancel}
          accessibilityRole="button"
          accessibilityLabel={t('common.cancel')}
        >
          <Icon name="xmark" size={13} color={colors.text_color_secondary} />
        </Pressable>
      </View>

      {/* Input */}
      <View style={[styles.inputWrapper, isFocused && styles.inputWrapperFocused]}>
        <TextInput
          ref={inputRef}
          style={styles.textInput}
          value={text}
          onChangeText={onChangeText}
          editable={!isCreating}
          accessibilityLabel={t('pages.memory.quickAddTitle')}
          placeholder={t('pages.memory.inputPlaceholder')}
          placeholderTextColor={colors.color_input_placeholder || colors.text_color_tertiary}
          maxLength={MEMORY_CHAR_LIMIT + 50}
          multiline
          numberOfLines={3}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyPress={handleKeyDown}
        />

        {/* Character count */}
        <View style={styles.inputMetaRow}>
          <Text size="xxs" color="tertiary">
            {isWeb ? t('pages.memory.saveShortcut') : ''}
          </Text>
          <Text size="xxs" color={charProps.color} style={charProps.style}>
            {text.length} / {MEMORY_CHAR_LIMIT}
          </Text>
        </View>
      </View>

      {/* Inspiration Chips */}
      <View style={styles.inspirationSection}>
        <Text size="xxs" weight="bold" color="tertiary" style={styles.inspirationLabel}>
          {t('pages.memory.inspirationTitle')}
        </Text>
        <View style={styles.chipsRow}>
          {inspirationChips.map((chip, idx) => (
            <Pressable
              key={idx}
              style={({ hovered }) => [
                styles.chip,
                hovered && styles.chipHovered,
              ]}
              accessibilityRole="button"
              disabled={isCreating}
              onPress={() => handleChipPress(chip)}
            >
              <Icon name="plus" size={10} color={colors.text_color_tertiary} />
              <Text size="xs" color="secondary" style={styles.chipText}>
                {chip}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>

      {/* Footer buttons */}
      <View style={styles.footer}>
        <Button
          text={t('common.cancel')}
          variant="secondary"
          isDisabled={isCreating}
          size="small"
          onPress={onCancel}
        />
        <Button
          text={t('pages.memory.addMemory')}
          prependIcon="plus"
          variant="primary"
          size="small"
          isDisabled={!canSubmit}
          isLoading={isCreating}
          onPress={handleSubmit}
        />
      </View>
    </View>
  );
}

const createStyles = (colors, memColors, isDark, isXS) =>
  StyleSheet.create({
    card: {
      borderRadius: BORDER_RADIUS.large,
      borderWidth: 1,
      borderColor: colors.border_color_input,
      backgroundColor: colors.solid_color_block,
      padding: isXS ? 14 : 16,
      marginBottom: 18,
      ...(isWeb
        ? {
            boxShadow: `0 4px 20px ${isDark ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.06)'}`,
          }
        : {}),
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 12,
    },
    headerTitleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      flex: 1,
      minWidth: 0,
    },
    iconCircle: {
      width: 28,
      height: 28,
      borderRadius: 14,
      backgroundColor: memColors.accentBg,
      alignItems: 'center',
      justifyContent: 'center',
    },
    closeButton: {
      minWidth: 40,
      minHeight: 40,
      alignItems: 'center',
      justifyContent: 'center',
      padding: 6,
      borderRadius: 8,
      ...(isWeb ? { cursor: 'pointer' } : {}),
    },
    closeButtonHovered: {
      backgroundColor: colors.solid_color_tertiary,
    },
    inputWrapper: {
      borderRadius: BORDER_RADIUS.medium,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_tertiary,
      padding: 12,
      marginBottom: 12,
    },
    inputWrapperFocused: {
      borderColor: colors.text_color_tertiary,
      backgroundColor: colors.solid_color_secondary,
    },
    textInput: {
      fontSize: 14,
      lineHeight: 20,
      color: colors.text_color_primary,
      minHeight: 68,
      textAlignVertical: 'top',
      ...(isWeb ? { outlineStyle: 'none' } : {}),
    },
    inputMetaRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: 8,
      paddingTop: 6,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: colors.border_color_secondary,
    },
    inspirationSection: {
      gap: 6,
      marginBottom: 14,
    },
    inspirationLabel: {
      letterSpacing: 0.5,
      textTransform: 'uppercase',
    },
    chipsRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 6,
    },
    chip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingVertical: 5,
      paddingHorizontal: 10,
      borderRadius: 14,
      backgroundColor: colors.solid_color_secondary,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      ...(isWeb ? { cursor: 'pointer', transition: 'all 0.16s ease' } : {}),
    },
    chipHovered: {
      borderColor: colors.border_color_input,
      backgroundColor: colors.solid_color_tertiary,
    },
    chipText: {
      fontSize: 12,
    },
    footer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 10,
    },
  });

export default React.memo(MemoryQuickAdd);
